import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
// REMOVED: ThemeProvider and createTheme - using ThemeContextProvider instead
import CssBaseline from '@mui/material/CssBaseline';
import { CircularProgress, Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AuthProvider } from './context/AuthContext';
import { StripeProvider } from './context/StripeContext';
import { ThemeContextProvider } from './context/ThemeContext';
import { CartProvider } from './context/CartContext';
import RecaptchaProvider from './context/RecaptchaProvider';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AutoLogoutWrapper from './components/auth/AutoLogoutWrapper';
import EmbeddedAuthHandler from './components/auth/EmbeddedAuthHandler';
import ErrorBoundary from './components/common/ErrorBoundary';
import CookieConsentBanner from './components/common/CookieConsentBanner';
import CustomDomainDetector from './components/common/CustomDomainDetector';
import useCookieConsent from './hooks/useCookieConsent';
import useOnboarding from './hooks/useOnboarding';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import OnboardingWizard from './components/onboarding/OnboardingWizard';
import AffiliateLandingPage from './components/AffiliateLandingPage';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import './styles/premium.css';
import './styles/animations.css';
import './i18n';

// Loading component
const PageLoader = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
      }}
    >
      <CircularProgress size={60} sx={{ color: theme.palette.common.white }} />
    </Box>
  );
};

// Theme-aware toast container
const ThemedToastContainer = () => {
  const theme = useTheme();
  return (
    <ToastContainer
      position="bottom-right"
      autoClose={5000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme={theme.palette.mode === 'dark' ? 'dark' : 'light'}
      toastStyle={{
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
      }}
    />
  );
};

const BookingRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/calendar${location.search}`} replace />;
};

// Host-based landing router: build.futuresenseai.com serves the
// website-first landing page; everything else gets the default landing.
const RootLanding = () => {
  if (typeof window !== 'undefined' && window.location.hostname === 'build.futuresenseai.com') {
    return <WebsitesLandingPage />;
  }
  return <LandingPage />;
};

const GroupEventsRedirect = () => {
  const [searchParams] = useSearchParams();
  const merged = new URLSearchParams(searchParams);
  merged.set('feature', 'group');
  return <Navigate to={`/advanced-features?${merged.toString()}`} replace />;
};

// Lazy loaded components (less frequently used or heavy components)
const DashboardPage = lazy(() => import('./pages/OptimizedDashboardPage'));
const MinimalDashboardPage = lazy(() => import('./pages/MinimalDashboardPage'));
const DashboardRouter = lazy(() => import('./components/dashboard/DashboardRouter'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
// const BookingPage = lazy(() => import('./pages/BookingPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const ClientsPage = lazy(() => import('./pages/ClientsPage'));
const ClientProfilePage = lazy(() => import('./pages/ClientProfilePage'));
const CRMPage = lazy(() => import('./pages/CRMPage'));
const LeadsPipelinePage = lazy(() => import('./pages/LeadsPipelinePage'));
const AutomationPage = lazy(() => import('./pages/AutomationPage'));
const SMSPage = lazy(() => import('./pages/SMSPage'));
const AdvancedFeaturesPage = lazy(() => import('./pages/AdvancedFeaturesPage'));
const WebsiteBuilderPage = lazy(() => import('./pages/WebsiteBuilderPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const WebsitesLandingPage = lazy(() => import('./pages/WebsitesLandingPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const EventsLandingPage = lazy(() => import('./pages/marketing/EventsLandingPage'));
const MedSpaLandingPage = lazy(() => import('./pages/marketing/MedSpaLandingPage'));
const VsMindbodyLandingPage = lazy(() => import('./pages/marketing/VsMindbodyLandingPage'));
const QAPage = lazy(() => import('./pages/QAPage'));
const ContactSalesPage = lazy(() => import('./pages/ContactSalesPage'));
// New Business Pages
// const AboutPage = lazy(() => import('./pages/AboutPage'));
// const PublicServicesPage = lazy(() => import('./pages/PublicServicesPage'));
// const ContactPage = lazy(() => import('./pages/ContactPage'));
// const PortfolioPage = lazy(() => import('./pages/PortfolioPage'));
// const BlogPage = lazy(() => import('./pages/BlogPage'));
// const CaseStudiesPage = lazy(() => import('./pages/CaseStudiesPage'));
// Blog pages
const SharedBlogList = lazy(() => import('./components/SharedBlogList'));
const SharedBlogPost = lazy(() => import('./components/SharedBlogPost'));
// Legal pages
const TermsOfService = lazy(() => import('./pages/legal/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const CookiePolicy = lazy(() => import('./pages/legal/CookiePolicy'));

// Test pages (only loaded in development)
// const BookingTestPage = lazy(() => import('./pages/BookingTestPage'));
// const PaymentTestPage = lazy(() => import('./pages/PaymentTestPage'));
const EmailTester = lazy(() => import('./components/email/EmailTester'));
const PaymentDashboardPage = lazy(() => import('./pages/PaymentDashboardPage'));
// const PublicBookingTestPage = lazy(() => import('./pages/PublicBookingTestPage'));
// const FunctionTest = lazy(() => import('./pages/FunctionTest'));
// const AdminFixPage = lazy(() => import('./pages/AdminFixPage'));
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage'));
const AdvertisePage = lazy(() => import('./pages/AdvertisePage'));
const AICallerPage = lazy(() => import('./pages/AICallerPage'));
const ProductManagementPage = lazy(() => import('./pages/ProductManagementPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const CheckoutSuccessPage = lazy(() => import('./pages/CheckoutSuccessPage'));
const CouponManagementPage = lazy(() => import('./pages/CouponManagementPage'));
// Receipt Scanner is a separate micro SaaS app - removed from Booker
// const ReceiptScannerPage = lazy(() => import('./pages/ReceiptScannerPage'));

// Components
const PublicBookingPage = lazy(() => import('./components/public/PublicBookingPage'));
const StandaloneBookingPage = lazy(() => import('./components/public/StandaloneBookingPage'));
const PublicWebsiteLayout = lazy(() => import('./components/public/PublicWebsiteLayout'));
const EnhancedPublicWebsiteLayout = lazy(() => import('./components/public/EnhancedPublicWebsiteLayout'));
const ChatWidget = lazy(() => import('./components/chatbot/ChatWidget'));
const AnimationExamples = lazy(() => import('./components/examples/AnimationExamples'));
const TicketValidationPage = lazy(() => import('./pages/TicketValidationPage'));
const DirectoryPage = lazy(() => import('./pages/DirectoryPage'));

// REMOVED: Inline theme configuration - using ThemeContextProvider instead
// ThemeContextProvider now handles all theme configuration from premiumTheme.js

// Cookie Consent Wrapper Component
const CookieConsentWrapper = ({ children }) => {
  const { showBanner, acceptAll, rejectAll, customize, loading } = useCookieConsent();

  if (loading) return children;

  return (
    <>
      {children}
      {showBanner && (
        <CookieConsentBanner
          onAccept={acceptAll}
          onReject={rejectAll}
          onCustomize={customize}
        />
      )}
    </>
  );
};

// Onboarding Wrapper Component
const OnboardingWrapper = ({ children }) => {
  const { showOnboarding, dismissOnboarding, completeOnboarding, loading } = useOnboarding();

  if (loading) return children;

  return (
    <>
      {children}
      <OnboardingWizard
        open={showOnboarding}
        onClose={dismissOnboarding}
        onComplete={completeOnboarding}
      />
    </>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <AuthProvider>
          <EmbeddedAuthHandler>
          <ThemeContextProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
            <CssBaseline />
            <StripeProvider>
              <CartProvider>
              <RecaptchaProvider>
                <Router>
                  <CustomDomainDetector>
                  <AutoLogoutWrapper>
                    <CookieConsentWrapper>
                      <OnboardingWrapper>
                        <Suspense fallback={<PageLoader />}>
                      <Routes>
                        {/* Public routes - eagerly loaded */}
                        <Route path="/" element={<RootLanding />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/signup" element={<SignupPage />} />
                        
                        {/* Public routes - lazy loaded */}
                          <Route path="/pricing" element={<PricingPage />} />
                        <Route path="/features" element={<FeaturesPage />} />
                        <Route path="/websites" element={<WebsitesLandingPage />} />
                        <Route path="/events" element={<EventsLandingPage />} />
                        <Route path="/for-medspa" element={<MedSpaLandingPage />} />
                        <Route path="/vs-mindbody" element={<VsMindbodyLandingPage />} />
                        <Route path="/faq" element={<FAQPage />} />
                        <Route path="/qa" element={<Navigate to="/faq" replace />} />
                        <Route path="/email-test" element={<ProtectedRoute><EmailTester /></ProtectedRoute>} />
                        <Route path="/contact-sales" element={<ContactSalesPage />} />
                        <Route path="/blog" element={<SharedBlogList />} />
                        <Route path="/blog/:blogId" element={<SharedBlogPost />} />
                        <Route path="/affiliate" element={<AffiliateLandingPage />} />
                        <Route path="/directory" element={<DirectoryPage />} />
                        {/* New Business Pages */}
                        {/* <Route path="/about" element={<AboutPage />} */} />
                        {/* <Route path="/services" element={<ServicesPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/portfolio" element={<PortfolioPage />} />
                        <Route path="/blog" element={<BlogPage />} />
                        {/* <Route path="/case-studies" element={<CaseStudiesPage />} />
                        {/* Legal Pages */}
                        <Route path="/legal/terms" element={<TermsOfService />} />
                        <Route path="/legal/privacy" element={<PrivacyPolicy />} />
                        <Route path="/legal/cookies" element={<CookiePolicy />} />
                        {/* Legacy redirects */}
                        <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
                        <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
                        <Route path="/cookie-policy" element={<Navigate to="/legal/cookies" replace />} />

                        {/* QR code ticket validation - public, no auth */}
                        <Route path="/validate-ticket" element={<TicketValidationPage />} />

                        {/* Standalone booking page - no website builder needed */}
                        <Route path="/s/:clientSlug" element={<StandaloneBookingPage />} />

                        {/* Public Website Routes - Multi-page business websites */}
                        <Route path="/book/:clientSlug/*" element={<EnhancedPublicWebsiteLayout />} />
                        
                        {/* Protected routes - all lazy loaded */}
                        <Route path="/dashboard" element={
                          <ProtectedRoute>
                            <DashboardRouter />
                          </ProtectedRoute>
                        } />
                        <Route path="/dashboard/payments" element={
                          <ProtectedRoute>
                            <PaymentDashboardPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/settings" element={
                          <ProtectedRoute>
                            <SettingsPage />
                          </ProtectedRoute>
                        } />
                        {/* Redirect /booking to /calendar for backward compatibility */}
                        <Route path="/booking" element={<BookingRedirect />} />
                        <Route path="/calendar" element={
                          <ProtectedRoute>
                            <CalendarPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/services" element={
                          <ProtectedRoute>
                            <ServicesPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/analytics" element={
                          <ProtectedRoute>
                            <AnalyticsPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/clients" element={
                          <ProtectedRoute>
                            <ClientsPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/clients/:clientEmail" element={
                          <ProtectedRoute>
                            <ClientProfilePage />
                          </ProtectedRoute>
                        } />
                        <Route path="/crm" element={
                          <ProtectedRoute>
                            <CRMPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/leads" element={
                          <ProtectedRoute>
                            <LeadsPipelinePage />
                          </ProtectedRoute>
                        } />
                        <Route path="/automation" element={
                          <ProtectedRoute>
                            <AutomationPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/sms" element={
                          <ProtectedRoute>
                            <SMSPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/group-events" element={
                          <ProtectedRoute>
                            <GroupEventsRedirect />
                          </ProtectedRoute>
                        } />
                        <Route path="/advanced-features" element={
                          <ProtectedRoute>
                            <AdvancedFeaturesPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/website-builder" element={
                          <ProtectedRoute>
                            <WebsiteBuilderPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/integrations" element={
                          <ProtectedRoute>
                            <IntegrationsPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/invoices" element={
                          <ProtectedRoute>
                            <InvoicesPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/advertise" element={
                          <ProtectedRoute>
                            <AdvertisePage />
                          </ProtectedRoute>
                        } />
                        <Route path="/ai-caller" element={
                          <ProtectedRoute>
                            <AICallerPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/products" element={
                          <ProtectedRoute>
                            <ProductManagementPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/checkout" element={<CheckoutPage />} />
                        <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                        <Route path="/checkout/cancel" element={<Navigate to="/checkout" replace />} />
                        <Route path="/coupons" element={
                          <ProtectedRoute>
                            <CouponManagementPage />
                          </ProtectedRoute>
                        } />
                        {/* Receipt Scanner is a separate micro SaaS app - removed from Booker */}
                        {/* <Route path="/receipt-scanner" element={
                          <ProtectedRoute>
                            <ReceiptScannerPage />
                          </ProtectedRoute>
                        } /> */}
                        
                        
                        {/* Test routes - only in development */}
                        {process.env.NODE_ENV === 'development' && (
                          <>
                            {/* Test pages commented out - not currently in use */}
                            <Route path="/animations" element={<AnimationExamples />} />
                          </>
                        )}
                        
                        {/* Catch all - redirect to landing */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>

                      {/* Chat Widget - lazy loaded */}
                      <ChatWidget />
                        </Suspense>
                      </OnboardingWrapper>
                    </CookieConsentWrapper>
                  </AutoLogoutWrapper>
                  </CustomDomainDetector>
                </Router>

                {/* Toast notifications - theme-aware */}
                <ThemedToastContainer />
              </RecaptchaProvider>
              </CartProvider>
          </StripeProvider>
            </LocalizationProvider>
        </ThemeContextProvider>
          </EmbeddedAuthHandler>
      </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
